import type { GeneralParameters } from '../../general/parameters/general'
import type { PlaybackSnapshot } from '../models/play-start-stop'
import type { PlayStartParameters } from '../parameters/play-start'

import { Analytix } from '~/shared/analytix'
import { AnalytixEvent } from '../../general/event'
import { Gate, seeds } from '../../general/gate'
import { PlayStartReason } from '../index.h'
import { parameters } from '../parameters/play-start'
import { getActivePlayerState } from '../players'

// Live content types with no meaningful "resume offset" on a fresh tune-in.
const LIVE_CONTENT_TYPES = ['tv', 'event']

// Whether a `play_start` reason resets `startSecond` to 0 on live content.
const RESETS_START_SECOND_BY_REASON: Record<PlayStartReason, boolean> = {
  userClick: true,
  autoPlay: true,
  autoPreview: true,
  userPlay: false, // `userPlay` is solved separately via `isFirstPlay`.
  userMove: false,
  userMoveIntro: false,
  userMoveCredits: false,
  bufferFull: false,
  appUp: false,
  userCast: false,
  userPip: false,
  advertisingStop: false,
  changeBitrate: false,
  changeAudio: false,
}

/**
 * Queue event wrapper for `play_start`.
 *
 * Opens play gate and freezes payload from the latest watcher snapshot /
 * player cached state to remain stable even when player internals change fast.
 */

export class PlayStartEvent extends AnalytixEvent<PlayStartParameters> {
  name = 'play_start'
  group = 'player'
  parameters = parameters
  sender = Analytix.prototype.sendPlayStartEvent

  // `play_start` event uses session gate to ensure that it is not emitted before session is started
  // `play_start` event uses player gate
  // `play_start` opens play gate to allow `play_stop` event to be emitted
  // error-only `play_start` closes play gate to avoid blocking next valid start
  gates = [
    Gate.Uses(seeds.session),
    Gate.Uses(seeds.player),
    // Gate.Opens(seeds.play), // added conditionally in constructor based on error state
  ]

  // `play_start` event should flush all `play_start` and `play_stop` events
  override flushing = ['play_start', 'play_stop']

  // do not expire
  override timeout = Infinity

  public reason: PlayStartReason
  private snapshot?: PlaybackSnapshot
  private isFirstPlay

  constructor(
    reason: PlayStartReason = PlayStartReason.userPlay,
    snapshot?: PlaybackSnapshot,
    isFirstPlay = false
  ) {
    super()

    this.reason = reason
    this.snapshot = snapshot
    this.isFirstPlay = isFirstPlay
    // if there are any error, `play_start` event will not open play gate at all
    // otherwise `play_start` event will open play gate for valid start
    if (this.snapshot?.httpErrorCode || this.snapshot?.contentErrorCode) {
      // no need to add quit gate here since play gate is not opened at all in error state
      // this.quitGates = [Gate.Closes(seeds.play)]
    } else {
      this.gates.push(Gate.Opens(seeds.play))
    }

    this.verify()
  }

  validate(): false | [GeneralParameters, PlayStartParameters] {
    const playerId = this.parameters.get('(playerId)')
    const player = getActivePlayerState(playerId)
    if (player == null) return false

    const parameters: PlayStartParameters = Object.fromEntries(this.parameters)
    parameters.contentPlayReasons = this.reason
    const startSecond = this.snapshot?.currentTime || player.currentTime
    const isLive = LIVE_CONTENT_TYPES.includes(
      String(this.parameters.get('contentType') ?? '')
    )
    const shouldResetStartSecond =
      RESETS_START_SECOND_BY_REASON[this.reason] ||
      (this.reason === PlayStartReason.userPlay && this.isFirstPlay)
    parameters.startSecond =
      isLive && shouldResetStartSecond ? '0' : startSecond
    parameters.contentLength =
      this.snapshot?.contentLength || player.contentLength
    parameters.audioTrackLanguage =
      this.snapshot?.audioTrackLanguage || player.audioTrackLanguage
    parameters.subtitlesLanguage =
      this.snapshot?.subtitlesLanguage || player.subtitlesLanguage
    parameters.videoQuality = this.snapshot?.videoQuality || player.videoQuality
    parameters.playbackSessionId =
      this.snapshot?.playbackSessionId || player.playbackSessionId

    // if there are any error, `play_start` event need to close previously opened play gate
    if (parameters.httpErrorCode || parameters.contentErrorCode) {
      this.quitGates = [Gate.Closes(seeds.play)]
    }

    // valid conditions for `play_start` event:
    // - `contentUrl` is available, which means content metadata pipeline already
    //   resolved base player_open/play_start parameters for current content
    const valid = this.parameters.get('contentUrl') != null //

    return (
      valid && [
        Object.fromEntries(this.general),
        parameters, // Object.fromEntries(this.parameters),
      ]
    )
  }
}
